import {
  attachClosestEdge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/types";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview";
import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { useSelector } from "react-redux";
import {
  DragData,
  ITickets,
  TicketGroup,
} from "../../containers/Tickets/types";
import { useDispatchMove } from "./useDispatchMove";
import { selectSelectedTickets } from "../../redux/selector";
//@ts-ignore
import { DragPreview } from "./DragPreview";

interface UseCardDragDropProps {
  ticket: ITickets;
  group: TicketGroup;
  subGroupId?: string;
  parentGroupId?: string;
  renderPreview?: (ticket: ITickets) => React.ReactNode;
}

export function useCardDragDrop({
  ticket,
  group,
  subGroupId,
  parentGroupId,
  renderPreview,
}: UseCardDragDropProps) {
  const ticketRef = useRef<HTMLDivElement>(null);
  const dispatchMove = useDispatchMove();
  const selectedTickets = useSelector(selectSelectedTickets);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
  const [draggingCount, setDraggingCount] = useState(0);

  const selRef = useRef(selectedTickets);
  useEffect(() => {
    selRef.current = selectedTickets;
  }, [selectedTickets]);

  useEffect(() => {
    if (!ticketRef.current) return;

    const currentIndex = group.ticketsList.findIndex(
      (t) => t._id === ticket._id,
    );

    const statusGroupId = parentGroupId ?? group._id;

    return combine(
      draggable({
        element: ticketRef.current,
        // @ts-ignore
        getInitialData: (): DragData => {
          const sel = selRef.current;
          const isSelected = sel?.id?.includes(ticket._id);

          return {
            ticketId: ticket._id,
            status: statusGroupId,
            subGroupId: subGroupId ?? null,
            index: currentIndex,
            selectedTicketIds: isSelected ? sel.id : [ticket._id],
            isMulti: !!(isSelected && sel.id.length > 1),
          };
        },
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          const sel = selRef.current;
          const count = sel?.id?.includes(ticket._id) ? sel.id.length : 1;
          setDraggingCount(count);
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({ x: "16px", y: "8px" }),
            render: ({ container }) => {
              const root = createRoot(container);
              root.render(
                <DragPreview
                  ticket={ticket}
                  count={count}
                  renderPreview={renderPreview}
                />,
              );
              return () => root.unmount();
            },
          });
        },
        onDragStart: () => {
          const sel = selRef.current;
          setDraggingCount(sel?.id?.includes(ticket._id) ? sel.id.length : 1);
        },
        onDrop: () => setDraggingCount(0),
      }),

      dropTargetForElements({
        element: ticketRef.current,
        getData: ({ input, element }) =>
          attachClosestEdge(
            {
              ticketId: ticket._id,
              status: statusGroupId,
              subGroupId: subGroupId ?? null,
              index: currentIndex,
            },
            { input, element, allowedEdges: ["top", "bottom"] },
          ),
        onDrag: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
        onDragLeave: () => setClosestEdge(null),
        onDrop: ({ source, self }) => {
          setClosestEdge(null);
          // @ts-ignore
          const data = source.data as DragData;
          if (!data?.ticketId) return;
          const edge = extractClosestEdge(self.data);
          const destinationIndex =
            edge === "top" ? currentIndex : currentIndex + 1;
          dispatchMove(
            data,
            statusGroupId,
            subGroupId ?? null,
            destinationIndex,
            { ticketId: ticket._id },
          );
        },
      }),
    );
  }, [ticket._id, group._id, subGroupId, parentGroupId, group.ticketsList]);

  return { ticketRef, closestEdge, draggingCount };
}
